import usePureState from '@/hooks/usePureState';
import connectedCalendarsService from '@/services/connectedCalendars.service';
import { IThirdPartyCalendarsWrite } from '@/types/apiClient/IEventTypeWrite';
import IConnectedCalendar from '@/types/IConnectedCalendar';
import { default as IService } from '@/types/IEventType';
import IHost from '@/types/IHost';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface IUseConnectedCalendarsProps {
	service: IService;
	onChange: (thirdPartyCalendars: IThirdPartyCalendarsWrite) => void;
}

export default function useConnectedCalendars(props: IUseConnectedCalendarsProps) {
	const { t } = useTranslation();

	const { state, dispatch } = usePureState({
		calendars: connectedCalendarsService.getCalendars(props.service.hosts),
		isShowingSecondaryCalendars: false,
	});

	const toSyncBookings = _getSyncCalendarsIds(props.service.hosts);
	const toVerifyAvailability = _getVerifyCalendarIds(props.service.hosts);

	function handleChangeRead(id: string, value: boolean) {
		// Remove the calendar from the list
		const _toVerifyAvailability = toVerifyAvailability.filter((c) => c.id !== id);

		// Only it has read permissions add it to the list
		if (value) {
			_toVerifyAvailability.push({ id });
		}

		return props.onChange({ toSyncBookings, toVerifyAvailability: _toVerifyAvailability });
	}

	function handleChangeWrite(id: string, value: boolean) {
		// Remove the calendar from the list
		const _toSyncBookings = toSyncBookings.filter((c) => c.id !== id);

		// Only it has write permissions add it to the list
		if (value) {
			_toSyncBookings.push({ id });
		}

		return props.onChange({ toSyncBookings: _toSyncBookings, toVerifyAvailability });
	}

	function handleFilterChange(e: ChangeEvent<HTMLInputElement>) {
		dispatch({ isShowingSecondaryCalendars: e.target.checked });
	}

	return {
		handleChangeRead,
		handleChangeWrite,
		handleFilterChange,
		t,
		calendars: state.calendars,
		isShowingSecondaryCalendars: state.isShowingSecondaryCalendars,
	};
}

function _getSyncCalendarsIds(hosts: IHost[]): { id: string }[] {
	const ids: { id: string }[] = [];
	hosts.forEach((host) => {
		host.calendars.forEach((calendar: IConnectedCalendar) => {
			if (calendar.syncBookings) {
				ids.push({ id: calendar.id });
			}
		});
	});

	return ids;
}

function _getVerifyCalendarIds(hosts: IHost[]): { id: string }[] {
	const ids: { id: string }[] = [];
	hosts.forEach((host) => {
		host.calendars.forEach((calendar: IConnectedCalendar) => {
			if (calendar.verifyAvailability) {
				ids.push({ id: calendar.id });
			}
		});
	});

	return ids;
}
